import { useEffect, useMemo, useState } from "react";
import Footer from "../components/Footer";
import Sidebar from "../components/Sidebar";
import Topbar from "../components/Topbar";
import { usePageTitle } from "../contexts/PageTitleContext";
import { certificateService } from "../services/api/certificate.service";
import { paymentService } from "../services/api/payment.service";
import { settingsService } from "../services/api/settings.service";
import {
  ApplicationCategory,
  type CertificateApplication,
} from "../types/certificate.types";
import { UserStatusId } from "../types/user.types";

const toDateInputValue = (date: Date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const getTodayDateInput = () => toDateInputValue(new Date());

const getMonthStartDateInput = () => {
  const now = new Date();
  return toDateInputValue(new Date(now.getFullYear(), now.getMonth(), 1));
};

const Dashboard = () => {
  const { setPageTitle } = usePageTitle();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [applications, setApplications] = useState<CertificateApplication[]>(
    [],
  );
  const [countyRangeStart, setCountyRangeStart] = useState<string>(
    getMonthStartDateInput(),
  );
  const [countyRangeEnd, setCountyRangeEnd] =
    useState<string>(getTodayDateInput());
  const [trendRangeStart, setTrendRangeStart] = useState<string>(
    getMonthStartDateInput(),
  );
  const [trendRangeEnd, setTrendRangeEnd] =
    useState<string>(getTodayDateInput());
  const [weddingCount, setWeddingCount] = useState<number | null>(null);
  const [divorceCount, setDivorceCount] = useState<number | null>(null);
  const [pendingWeddingCount, setPendingWeddingCount] = useState<number | null>(
    null,
  );
  const [pendingDivorceCount, setPendingDivorceCount] = useState<number | null>(
    null,
  );
  const [revenueWedding, setRevenueWedding] = useState<number | null>(null);
  const [revenueDivorce, setRevenueDivorce] = useState<number | null>(null);
  const [defaultCurrency, setDefaultCurrency] = useState<string>("USD");
  const [reprintWeddingCount, setReprintWeddingCount] = useState<number | null>(
    null,
  );
  const [reprintDivorceCount, setReprintDivorceCount] = useState<number | null>(
    null,
  );

  useEffect(() => {
    setPageTitle("Dashboard", "");
    const isDesktop = window.matchMedia("(min-width: 881px)").matches;
    setSidebarOpen(isDesktop);

    const handleResize = () => {
      const isDesktopView = window.matchMedia("(min-width: 881px)").matches;
      setSidebarOpen(isDesktopView);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [setPageTitle]);

  useEffect(() => {
    const fetchCounts = async () => {
      try {
        const [all, payments, settings] = await Promise.all([
          certificateService.getCertificateApplications(),
          paymentService.getPayments(),
          settingsService.getSettings(),
        ]);
        setApplications(all);

        const currencySetting = settings.find(
          (s) =>
            s.category === "general" &&
            (s.key === "defaultCurrency" || s.key === "currency"),
        );
        if (currencySetting?.value?.trim()) {
          setDefaultCurrency(currencySetting.value.trim().toUpperCase());
        }

        const processed = all.filter(
          (a) => a.statusId === UserStatusId.Completed,
        );
        const pending = all.filter(
          (a) =>
            a.statusId === UserStatusId.Created ||
            a.statusId === UserStatusId.Submitted ||
            a.statusId === UserStatusId.Queried ||
            a.statusId === UserStatusId.InProgress,
        );
        const reprint = all.filter((a) => a.statusId === UserStatusId.Printed);
        const appCategoryById = new Map(
          all.map((a) => [a.id, a.category] as const),
        );
        const weddingRevenueTotal = payments
          .filter((payment) => {
            const category =
              payment.certificateApplication?.category ||
              appCategoryById.get(payment.certificateApplicationId);
            return category === ApplicationCategory.Marriage;
          })
          .reduce(
            (sum, payment) =>
              sum + (payment.lraPayment || 0) + (payment.operationalCost || 0),
            0,
          );
        const divorceRevenueTotal = payments
          .filter((payment) => {
            const category =
              payment.certificateApplication?.category ||
              appCategoryById.get(payment.certificateApplicationId);
            return category === ApplicationCategory.Divorce;
          })
          .reduce(
            (sum, payment) =>
              sum + (payment.lraPayment || 0) + (payment.operationalCost || 0),
            0,
          );

        setWeddingCount(
          processed.filter((a) => a.category === ApplicationCategory.Marriage)
            .length,
        );
        setDivorceCount(
          processed.filter((a) => a.category === ApplicationCategory.Divorce)
            .length,
        );
        setPendingWeddingCount(
          pending.filter((a) => a.category === ApplicationCategory.Marriage)
            .length,
        );
        setPendingDivorceCount(
          pending.filter((a) => a.category === ApplicationCategory.Divorce)
            .length,
        );
        setReprintWeddingCount(
          reprint.filter((a) => a.category === ApplicationCategory.Marriage)
            .length,
        );
        setReprintDivorceCount(
          reprint.filter((a) => a.category === ApplicationCategory.Divorce)
            .length,
        );
        setRevenueWedding(weddingRevenueTotal);
        setRevenueDivorce(divorceRevenueTotal);
      } catch {
        setApplications([]);
        setWeddingCount(0);
        setDivorceCount(0);
        setPendingWeddingCount(0);
        setPendingDivorceCount(0);
        setReprintWeddingCount(0);
        setReprintDivorceCount(0);
        setRevenueWedding(0);
        setRevenueDivorce(0);
        setDefaultCurrency("USD");
      }
    };
    fetchCounts();
  }, []);

  const formatCurrency = (amount: number): string => {
    try {
      return new Intl.NumberFormat(undefined, {
        style: "currency",
        currency: defaultCurrency,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(amount);
    } catch {
      return `${defaultCurrency} ${amount.toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`;
    }
  };

  const trendChartData = useMemo(() => {
    const startDate = new Date(`${trendRangeStart}T00:00:00`);
    const endDate = new Date(`${trendRangeEnd}T00:00:00`);

    if (
      Number.isNaN(startDate.getTime()) ||
      Number.isNaN(endDate.getTime()) ||
      startDate > endDate
    ) {
      return {
        chartWidth: 600,
        chartHeight: 220,
        padding: { top: 16, right: 20, bottom: 32, left: 30 },
        maxY: 1,
        points: [] as Array<{ date: Date; marriage: number; divorce: number }>,
        weddingLine: "",
        divorceLine: "",
        xForIndex: (_index: number) => 0,
        yForValue: (_value: number) => 0,
        formatLabel: (date: Date) => `${date.getDate()}/${date.getMonth() + 1}`,
        startDate,
        endDate,
      };
    }

    const rangeDays =
      Math.floor((endDate.getTime() - startDate.getTime()) / 86_400_000) + 1;

    const buckets = new Map<
      string,
      { date: Date; marriage: number; divorce: number }
    >();

    for (let index = 0; index < rangeDays; index += 1) {
      const date = new Date(startDate);
      date.setDate(startDate.getDate() + index);
      const key = date.toISOString().split("T")[0];
      buckets.set(key, { date, marriage: 0, divorce: 0 });
    }

    applications.forEach((application) => {
      if (!application.createdAt) return;
      const createdDate = new Date(application.createdAt);
      createdDate.setHours(0, 0, 0, 0);
      if (createdDate < startDate || createdDate > endDate) return;
      const key = createdDate.toISOString().split("T")[0];
      const bucket = buckets.get(key);
      if (!bucket) return;

      if (application.category === ApplicationCategory.Marriage) {
        bucket.marriage += 1;
      } else if (application.category === ApplicationCategory.Divorce) {
        bucket.divorce += 1;
      }
    });

    const points = Array.from(buckets.values());
    const maxY = Math.max(
      1,
      ...points.map((point) => Math.max(point.marriage, point.divorce)),
    );

    const chartWidth = 600;
    const chartHeight = 220;
    const padding = { top: 16, right: 20, bottom: 32, left: 30 };
    const drawableWidth = chartWidth - padding.left - padding.right;
    const drawableHeight = chartHeight - padding.top - padding.bottom;

    const xForIndex = (index: number) =>
      points.length > 1
        ? padding.left + (index / (points.length - 1)) * drawableWidth
        : padding.left + drawableWidth / 2;

    const yForValue = (value: number) =>
      padding.top + (1 - value / maxY) * drawableHeight;

    const weddingLine = points
      .map((point, index) => `${xForIndex(index)},${yForValue(point.marriage)}`)
      .join(" ");

    const divorceLine = points
      .map((point, index) => `${xForIndex(index)},${yForValue(point.divorce)}`)
      .join(" ");

    const formatLabel = (date: Date) =>
      `${date.getDate()}/${date.getMonth() + 1}`;

    return {
      chartWidth,
      chartHeight,
      padding,
      maxY,
      points,
      weddingLine,
      divorceLine,
      xForIndex,
      yForValue,
      formatLabel,
      startDate,
      endDate,
    };
  }, [applications, trendRangeEnd, trendRangeStart]);

  const countyChartData = useMemo(() => {
    const startDate = new Date(`${countyRangeStart}T00:00:00`);
    const endDate = new Date(`${countyRangeEnd}T23:59:59`);

    if (
      Number.isNaN(startDate.getTime()) ||
      Number.isNaN(endDate.getTime()) ||
      startDate > endDate
    ) {
      return {
        items: [] as Array<{
          county: string;
          marriage: number;
          divorce: number;
          total: number;
        }>,
        maxY: 1,
        chartWidth: 640,
        chartHeight: 260,
        padding: { top: 16, right: 20, bottom: 44, left: 34 },
        groupWidth: 0,
        barWidth: 0,
        barGap: 0,
        labelStep: 1,
      };
    }

    const byCounty = new Map<string, { marriage: number; divorce: number }>();

    applications.forEach((application) => {
      if (!application.createdAt) return;

      const createdAt = new Date(application.createdAt);
      if (Number.isNaN(createdAt.getTime())) return;
      if (createdAt < startDate || createdAt > endDate) return;

      const countyName =
        application.countyName || application.county?.name || "Unspecified";
      const current = byCounty.get(countyName) || { marriage: 0, divorce: 0 };

      if (application.category === ApplicationCategory.Marriage) {
        current.marriage += 1;
      } else if (application.category === ApplicationCategory.Divorce) {
        current.divorce += 1;
      }

      byCounty.set(countyName, current);
    });

    const items = Array.from(byCounty.entries())
      .map(([county, values]) => ({
        county,
        marriage: values.marriage,
        divorce: values.divorce,
        total: values.marriage + values.divorce,
      }))
      .sort(
        (left, right) =>
          right.total - left.total || left.county.localeCompare(right.county),
      );

    const maxY = Math.max(
      1,
      ...items.map((item) => Math.max(item.marriage, item.divorce)),
    );

    const padding = { top: 16, right: 20, bottom: 44, left: 34 };
    const chartWidth = 640;
    const chartHeight = 260;
    const drawableWidth = chartWidth - padding.left - padding.right;
    const groupWidth =
      items.length > 0 ? drawableWidth / items.length : drawableWidth;
    const barWidth = Math.max(4, Math.min(18, groupWidth * 0.32));
    const barGap = Math.max(2, Math.min(10, groupWidth * 0.12));
    const labelStep = Math.max(1, Math.ceil(items.length / 8));

    return {
      items,
      maxY,
      chartWidth,
      chartHeight,
      padding,
      groupWidth,
      barWidth,
      barGap,
      labelStep,
    };
  }, [applications, countyRangeEnd, countyRangeStart]);

  const cardVariants = {
    processed: {
      cardBackground:
        "linear-gradient(135deg, rgba(30,64,175,0.14) 0%, rgba(255,255,255,0.96) 50%, rgba(220,38,38,0.10) 100%)",
      shadow: "0 10px 24px rgba(30,64,175,0.10)",
      gridBackground: "rgba(255, 255, 255, 0.90)",
      totalBackground:
        "linear-gradient(180deg, rgba(37,99,235,0.16) 0%, rgba(255,255,255,0.56) 52%, rgba(220,38,38,0.10) 100%)",
      totalColor: "#1e3a8a",
      labelBackground: "rgba(37,99,235,0.10)",
      valueBackground: "rgba(59,130,246,0.07)",
    },
    pending: {
      cardBackground:
        "linear-gradient(135deg, rgba(234,88,12,0.16) 0%, rgba(255,255,255,0.96) 52%, rgba(220,38,38,0.10) 100%)",
      shadow: "0 10px 24px rgba(234,88,12,0.10)",
      gridBackground: "rgba(255, 251, 245, 0.92)",
      totalBackground:
        "linear-gradient(180deg, rgba(251,146,60,0.24) 0%, rgba(255,255,255,0.62) 52%, rgba(239,68,68,0.12) 100%)",
      totalColor: "#9a3412",
      labelBackground: "rgba(251,146,60,0.14)",
      valueBackground: "rgba(254,215,170,0.38)",
    },
    revenue: {
      cardBackground:
        "linear-gradient(135deg, rgba(22,163,74,0.15) 0%, rgba(255,255,255,0.96) 50%, rgba(59,130,246,0.10) 100%)",
      shadow: "0 10px 24px rgba(22,163,74,0.10)",
      gridBackground: "rgba(247, 254, 231, 0.74)",
      totalBackground:
        "linear-gradient(180deg, rgba(74,222,128,0.24) 0%, rgba(255,255,255,0.62) 52%, rgba(59,130,246,0.12) 100%)",
      totalColor: "#166534",
      labelBackground: "rgba(74,222,128,0.16)",
      valueBackground: "rgba(187,247,208,0.40)",
    },
    reprint: {
      cardBackground:
        "linear-gradient(135deg, rgba(109,40,217,0.14) 0%, rgba(255,255,255,0.96) 52%, rgba(37,99,235,0.10) 100%)",
      shadow: "0 10px 24px rgba(109,40,217,0.10)",
      gridBackground: "rgba(250, 245, 255, 0.92)",
      totalBackground:
        "linear-gradient(180deg, rgba(168,85,247,0.22) 0%, rgba(255,255,255,0.60) 52%, rgba(59,130,246,0.12) 100%)",
      totalColor: "#5b21b6",
      labelBackground: "rgba(196,181,253,0.26)",
      valueBackground: "rgba(221,214,254,0.34)",
    },
  };

  const baseGridStyle = {
    display: "grid",
    gridTemplateColumns: "1fr 2fr",
    marginTop: "0.5rem",
    border: "1px solid #d1d5db",
    borderRadius: "10px",
    overflow: "hidden",
  };

  const baseTotalCellStyle = {
    gridRow: "1 / span 4",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "2rem",
    fontWeight: 700,
  };

  const baseLabelCellStyle = {
    textAlign: "center" as const,
    padding: "0.35rem",
    fontWeight: 600,
    color: "#1f2937",
  };

  const baseValueCellStyle = {
    textAlign: "center" as const,
    padding: "0.35rem",
    fontSize: "1.4rem",
    color: "#111827",
  };

  const renderSplitStatCard = (
    title: string,
    marriageValue: number | null,
    divorceValue: number | null,
    variantKey: keyof typeof cardVariants,
  ) => {
    const isRevenueCard = variantKey === "revenue";
    const totalValue = (marriageValue ?? 0) + (divorceValue ?? 0);
    const formatValue = (value: number) =>
      isRevenueCard ? formatCurrency(value) : value.toLocaleString();

    return (
      <article
        className="stat-card"
        style={{
          border: "2px solid rgba(21, 21, 23, 0.18)",
          background: cardVariants[variantKey].cardBackground,
          boxShadow: cardVariants[variantKey].shadow,
        }}
      >
        <h3>{title}</h3>
        <div
          style={{
            ...baseGridStyle,
            background: cardVariants[variantKey].gridBackground,
          }}
        >
          <div
            style={{
              ...baseTotalCellStyle,
              background: cardVariants[variantKey].totalBackground,
              color: cardVariants[variantKey].totalColor,
              fontSize: isRevenueCard ? "1.2rem" : baseTotalCellStyle.fontSize,
            }}
          >
            {marriageValue === null && divorceValue === null
              ? "…"
              : formatValue(totalValue)}
          </div>
          <div
            style={{
              ...baseLabelCellStyle,
              background: cardVariants[variantKey].labelBackground,
              color: "#1e3a8a",
            }}
          >
            Marriage
          </div>
          <div
            style={{
              ...baseValueCellStyle,
              background: cardVariants[variantKey].valueBackground,
              color: "#1e3a8a",
              fontSize: isRevenueCard ? "1rem" : baseValueCellStyle.fontSize,
            }}
          >
            {marriageValue === null ? "…" : formatValue(marriageValue)}
          </div>
          <div
            style={{
              ...baseLabelCellStyle,
              background: cardVariants[variantKey].labelBackground,
              color: "#7f1d1d",
            }}
          >
            Divorce
          </div>
          <div
            style={{
              ...baseValueCellStyle,
              background: cardVariants[variantKey].valueBackground,
              color: "#7f1d1d",
              fontSize: isRevenueCard ? "1rem" : baseValueCellStyle.fontSize,
            }}
          >
            {divorceValue === null ? "…" : formatValue(divorceValue)}
          </div>
        </div>
      </article>
    );
  };

  return (
    <div
      className={`dashboard-body ${sidebarOpen ? "sidebar-open" : "sidebar-closed"}`}
    >
      <Topbar
        onMenuToggle={() => setSidebarOpen((prev) => !prev)}
        sidebarOpen={sidebarOpen}
      />
      <Sidebar isOpen={sidebarOpen} />
      <main className="main-content">
        <section className="stats-grid">
          {renderSplitStatCard(
            "Pending Certificates",
            pendingWeddingCount,
            pendingDivorceCount,
            "pending",
          )}
          {renderSplitStatCard(
            "Processed Certificates",
            weddingCount,
            divorceCount,
            "processed",
          )}

          {renderSplitStatCard(
            `Revenue (${defaultCurrency})`,
            revenueWedding,
            revenueDivorce,
            "revenue",
          )}
          {renderSplitStatCard(
            "Reprint",
            reprintWeddingCount,
            reprintDivorceCount,
            "reprint",
          )}
        </section>

        <section className="content-grid">
          <article className="panel">
            <h3>Certificates Processing Trend</h3>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))",
                gap: "0.6rem",
                marginBottom: "0.85rem",
              }}
            >
              <label
                style={{
                  display: "grid",
                  gap: "0.2rem",
                  fontSize: "0.82rem",
                  color: "#334155",
                }}
              >
                From
                <input
                  type="date"
                  value={trendRangeStart}
                  max={trendRangeEnd}
                  onChange={(event) => setTrendRangeStart(event.target.value)}
                />
              </label>
              <label
                style={{
                  display: "grid",
                  gap: "0.2rem",
                  fontSize: "0.82rem",
                  color: "#334155",
                }}
              >
                To
                <input
                  type="date"
                  value={trendRangeEnd}
                  min={trendRangeStart}
                  onChange={(event) => setTrendRangeEnd(event.target.value)}
                />
              </label>
            </div>

            <div style={{ width: "100%", overflow: "hidden" }}>
              <svg
                viewBox={`0 0 ${trendChartData.chartWidth} ${trendChartData.chartHeight}`}
                style={{ width: "100%", height: "auto", display: "block" }}
                role="img"
                aria-label="Certificate processing trend line chart"
              >
                {[0, 0.25, 0.5, 0.75, 1].map((ratio) => {
                  const y =
                    trendChartData.padding.top +
                    ratio *
                      (trendChartData.chartHeight -
                        trendChartData.padding.top -
                        trendChartData.padding.bottom);
                  return (
                    <line
                      key={`grid-${ratio}`}
                      x1={trendChartData.padding.left}
                      y1={y}
                      x2={
                        trendChartData.chartWidth - trendChartData.padding.right
                      }
                      y2={y}
                      stroke="#e2e8f0"
                      strokeWidth="1"
                    />
                  );
                })}

                {trendChartData.weddingLine && (
                  <polyline
                    fill="none"
                    stroke="#2563eb"
                    strokeWidth="3"
                    points={trendChartData.weddingLine}
                    strokeLinejoin="round"
                    strokeLinecap="round"
                  />
                )}
                {trendChartData.divorceLine && (
                  <polyline
                    fill="none"
                    stroke="#dc2626"
                    strokeWidth="3"
                    points={trendChartData.divorceLine}
                    strokeLinejoin="round"
                    strokeLinecap="round"
                  />
                )}

                {trendChartData.points.map((point, index) => {
                  const x = trendChartData.xForIndex(index);
                  return (
                    <g key={point.date.toISOString()}>
                      <circle
                        cx={x}
                        cy={trendChartData.yForValue(point.marriage)}
                        r="3.5"
                        fill="#2563eb"
                      />
                      <circle
                        cx={x}
                        cy={trendChartData.yForValue(point.divorce)}
                        r="3.5"
                        fill="#dc2626"
                      />
                      {(index === 0 ||
                        index === trendChartData.points.length - 1 ||
                        index %
                          Math.max(
                            1,
                            Math.ceil(trendChartData.points.length / 6),
                          ) ===
                          0) && (
                        <text
                          x={x}
                          y={trendChartData.chartHeight - 8}
                          textAnchor="middle"
                          fontSize="10"
                          fill="#64748b"
                        >
                          {trendChartData.formatLabel(point.date)}
                        </text>
                      )}
                    </g>
                  );
                })}

                <text
                  x={trendChartData.padding.left}
                  y={12}
                  fontSize="10"
                  fill="#64748b"
                >
                  Max/day: {trendChartData.maxY}
                </text>
              </svg>
            </div>

            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginTop: "0.5rem",
                gap: "1rem",
                flexWrap: "wrap",
                fontSize: "0.85rem",
              }}
            >
              <div
                style={{ display: "flex", gap: "1rem", alignItems: "center" }}
              >
                <span
                  style={{
                    display: "inline-flex",
                    gap: "0.35rem",
                    alignItems: "center",
                    color: "#1e3a8a",
                  }}
                >
                  <span
                    style={{
                      width: "10px",
                      height: "10px",
                      borderRadius: "999px",
                      background: "#2563eb",
                      display: "inline-block",
                    }}
                  />
                  Marriage
                </span>
                <span
                  style={{
                    display: "inline-flex",
                    gap: "0.35rem",
                    alignItems: "center",
                    color: "#991b1b",
                  }}
                >
                  <span
                    style={{
                      width: "10px",
                      height: "10px",
                      borderRadius: "999px",
                      background: "#dc2626",
                      display: "inline-block",
                    }}
                  />
                  Divorce
                </span>
              </div>
              <span style={{ color: "#64748b" }}>
                {trendChartData.startDate.toLocaleDateString()} -{" "}
                {trendChartData.endDate.toLocaleDateString()}
              </span>
            </div>
          </article>
          <article className="panel">
            <h3>Applications Per County</h3>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))",
                gap: "0.6rem",
                marginBottom: "0.85rem",
              }}
            >
              <label
                style={{
                  display: "grid",
                  gap: "0.2rem",
                  fontSize: "0.82rem",
                  color: "#334155",
                }}
              >
                From
                <input
                  type="date"
                  value={countyRangeStart}
                  max={countyRangeEnd}
                  onChange={(event) => setCountyRangeStart(event.target.value)}
                />
              </label>
              <label
                style={{
                  display: "grid",
                  gap: "0.2rem",
                  fontSize: "0.82rem",
                  color: "#334155",
                }}
              >
                To
                <input
                  type="date"
                  value={countyRangeEnd}
                  min={countyRangeStart}
                  onChange={(event) => setCountyRangeEnd(event.target.value)}
                />
              </label>
            </div>

            <div style={{ width: "100%", overflow: "hidden" }}>
              <svg
                viewBox={`0 0 ${countyChartData.chartWidth} ${countyChartData.chartHeight}`}
                style={{ width: "100%", height: "auto", display: "block" }}
                role="img"
                aria-label="Applications per county bar chart"
              >
                {[0, 0.25, 0.5, 0.75, 1].map((ratio) => {
                  const y =
                    countyChartData.padding.top +
                    ratio *
                      (countyChartData.chartHeight -
                        countyChartData.padding.top -
                        countyChartData.padding.bottom);
                  return (
                    <line
                      key={`county-grid-${ratio}`}
                      x1={countyChartData.padding.left}
                      y1={y}
                      x2={
                        countyChartData.chartWidth -
                        countyChartData.padding.right
                      }
                      y2={y}
                      stroke="#e2e8f0"
                      strokeWidth="1"
                    />
                  );
                })}

                {countyChartData.items.map((item, index) => {
                  const drawableHeight =
                    countyChartData.chartHeight -
                    countyChartData.padding.top -
                    countyChartData.padding.bottom;
                  const groupWidth = countyChartData.groupWidth;
                  const barWidth = countyChartData.barWidth;
                  const gap = countyChartData.barGap;
                  const groupInnerWidth = barWidth * 2 + gap;
                  const xOffset = Math.max(
                    0,
                    (groupWidth - groupInnerWidth) / 2,
                  );
                  const xStart =
                    countyChartData.padding.left + index * groupWidth + xOffset;

                  const weddingHeight =
                    (item.marriage / countyChartData.maxY) * drawableHeight;
                  const divorceHeight =
                    (item.divorce / countyChartData.maxY) * drawableHeight;

                  const weddingY =
                    countyChartData.padding.top +
                    (drawableHeight - weddingHeight);
                  const divorceY =
                    countyChartData.padding.top +
                    (drawableHeight - divorceHeight);

                  const countyLabel =
                    item.county.length > 10
                      ? `${item.county.slice(0, 10)}…`
                      : item.county;

                  return (
                    <g key={item.county}>
                      <rect
                        x={xStart}
                        y={weddingY}
                        width={barWidth}
                        height={Math.max(1, weddingHeight)}
                        fill="#2563eb"
                        rx="2"
                      >
                        <title>{`${item.county} - Marriage: ${item.marriage}`}</title>
                      </rect>
                      <rect
                        x={xStart + barWidth + gap}
                        y={divorceY}
                        width={barWidth}
                        height={Math.max(1, divorceHeight)}
                        fill="#dc2626"
                        rx="2"
                      >
                        <title>{`${item.county} - Divorce: ${item.divorce}`}</title>
                      </rect>
                      {(index % countyChartData.labelStep === 0 ||
                        index === countyChartData.items.length - 1) && (
                        <text
                          x={xStart + barWidth + gap / 2}
                          y={countyChartData.chartHeight - 8}
                          textAnchor="middle"
                          fontSize="10"
                          fill="#64748b"
                        >
                          {countyLabel}
                        </text>
                      )}
                    </g>
                  );
                })}

                <text
                  x={countyChartData.padding.left}
                  y={12}
                  fontSize="10"
                  fill="#64748b"
                >
                  Max/county: {countyChartData.maxY}
                </text>
              </svg>
            </div>

            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginTop: "0.5rem",
                gap: "1rem",
                flexWrap: "wrap",
                fontSize: "0.85rem",
              }}
            >
              <div
                style={{ display: "flex", gap: "1rem", alignItems: "center" }}
              >
                <span
                  style={{
                    display: "inline-flex",
                    gap: "0.35rem",
                    alignItems: "center",
                    color: "#1e3a8a",
                  }}
                >
                  <span
                    style={{
                      width: "10px",
                      height: "10px",
                      borderRadius: "2px",
                      background: "#2563eb",
                      display: "inline-block",
                    }}
                  />
                  Marriage
                </span>
                <span
                  style={{
                    display: "inline-flex",
                    gap: "0.35rem",
                    alignItems: "center",
                    color: "#991b1b",
                  }}
                >
                  <span
                    style={{
                      width: "10px",
                      height: "10px",
                      borderRadius: "2px",
                      background: "#dc2626",
                      display: "inline-block",
                    }}
                  />
                  Divorce
                </span>
              </div>
              <span style={{ color: "#64748b" }}>
                {new Date(`${countyRangeStart}T00:00:00`).toLocaleDateString()}{" "}
                - {new Date(`${countyRangeEnd}T00:00:00`).toLocaleDateString()}
              </span>
            </div>
          </article>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default Dashboard;
